import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw,
  Search, Building2, ChevronDown, ChevronUp, User, Megaphone, Activity,
  Wrench, Copy, ExternalLink, Phone
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { fetchWithCsrf } from '../lib/fetchWithCsrf';

// A labeled, copyable block of corrected campaign copy for console resubmission.
function CopyText({ label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <div className="border border-gray-200 rounded-lg p-2.5 bg-white">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-medium text-gray-500">{label}</span>
        <button
          type="button"
          onClick={async () => {
            try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { /* manual select */ }
          }}
          className="text-[11px] text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
        >
          <Copy className="w-3 h-3" /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="whitespace-pre-wrap text-[11px] text-gray-700 font-sans">{value}</pre>
    </div>
  );
}

// ─── Status badge helpers ─────────────────────────────────────────────────────

const STATUS_CONFIG = {
  approved:          { label: 'Approved',      color: 'bg-green-100 text-green-800'  },
  'twilio-approved': { label: 'Approved',      color: 'bg-green-100 text-green-800'  },
  verified:          { label: 'Verified',      color: 'bg-green-100 text-green-800'  },
  active:            { label: 'Active',        color: 'bg-green-100 text-green-800'  },
  'pending-review':  { label: 'Pending Review',color: 'bg-yellow-100 text-yellow-800'},
  pending:           { label: 'Pending',       color: 'bg-yellow-100 text-yellow-800'},
  draft:             { label: 'Draft',         color: 'bg-gray-100 text-gray-700'    },
  failed:            { label: 'Failed',        color: 'bg-red-100 text-red-800'      },
  rejected:          { label: 'Rejected',      color: 'bg-red-100 text-red-800'      },
  suspended:         { label: 'Suspended',     color: 'bg-red-100 text-red-800'      },
};

function StatusBadge({ status }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
        Not Started
      </span>
    );
  }

  const key = status.toLowerCase();
  const cfg = STATUS_CONFIG[key] || { label: status, color: 'bg-gray-100 text-gray-700' };
  const isGood    = cfg.color.includes('green');
  const isBad     = cfg.color.includes('red');
  const isPending = cfg.color.includes('yellow');

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      {isGood    && <CheckCircle className="w-3 h-3" />}
      {isBad     && <XCircle className="w-3 h-3" />}
      {isPending && <Clock className="w-3 h-3" />}
      {cfg.label}
    </span>
  );
}

// ─── Progress dots ────────────────────────────────────────────────────────────

function StageProgress({ tenant }) {
  const getState = (status) => {
    if (!status) return 'none';
    const s = status.toLowerCase();
    if (['approved','twilio-approved','verified','active'].includes(s)) return 'approved';
    if (['failed','rejected','suspended'].includes(s)) return 'failed';
    return 'pending';
  };

  const states = [
    getState(tenant.customer_profile?.status),
    getState(tenant.brand?.status),
    getState(tenant.campaigns?.[0]?.status),
  ];

  const labels = ['Profile', 'Brand', 'Campaign'];

  return (
    <div className="flex items-center gap-1">
      {states.map((state, i) => {
        let dot = 'w-2.5 h-2.5 rounded-full ';
        if (state === 'approved') dot += 'bg-green-500';
        else if (state === 'failed') dot += 'bg-red-500';
        else if (state === 'pending') dot += 'bg-yellow-400';
        else dot += 'bg-gray-300';

        return (
          <React.Fragment key={labels[i]}>
            <div title={`${labels[i]}: ${state === 'none' ? 'Not started' : state}`} className={dot} />
            {i < 2 && (
              <div className={`w-4 h-px ${state === 'approved' ? 'bg-green-300' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Expanded detail ──────────────────────────────────────────────────────────

function TenantDetail({ tenant, onResubmitted }) {
  const [repairing, setRepairing] = useState(false);   // submitting to carriers
  const [preparing, setPreparing] = useState(false);   // loading the review form pre-fill
  const [repair, setRepair] = useState(null);          // submit result { mode, message, fields, consoleUrl }
  const [form, setForm] = useState(null);              // review form: { useCase, description, messageFlow, sampleMessages, flags... }
  const [checking, setChecking] = useState(false);
  const [consentCheck, setConsentCheck] = useState(null); // { ready, scanned, checks, ... } | { error }
  const [attaching, setAttaching] = useState(false);
  const [attachResult, setAttachResult] = useState(null); // { ok, message }
  const failedCampaign = tenant.campaigns.find(
    c => c.status && ['failed', 'rejected'].includes(c.status.toLowerCase())
  );
  const approvedCampaign = tenant.campaigns.find(
    c => c.status && ['verified', 'approved', 'active'].includes(c.status.toLowerCase())
  );

  // Attach the tenant's number to its campaign's messaging-service sender pool
  // (for legacy/edge tenants the cron auto-assign skips). Idempotent server-side.
  const attachNumber = async () => {
    setAttaching(true);
    setAttachResult(null);
    try {
      const res = await fetchWithCsrf(`/api/admin/a2p/attach-number/${tenant.tenant_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'assign' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || data.error || `HTTP ${res.status}`);
      setAttachResult({ ok: true, message: data.message });
    } catch (err) {
      setAttachResult({ ok: false, message: err.message });
    } finally {
      setAttaching(false);
    }
  };

  // Live re-scan the customer's site so you can confirm they actually added the
  // consent language BEFORE burning a resubmit.
  const checkConsent = async () => {
    setChecking(true);
    setConsentCheck(null);
    try {
      const res = await fetchWithCsrf(`/api/admin/a2p/check-consent/${tenant.tenant_id}`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      setConsentCheck(data);
    } catch (err) {
      setConsentCheck({ error: err.message });
    } finally {
      setChecking(false);
    }
  };

  const consentMissing = consentCheck && !consentCheck.error && consentCheck.scanned && !consentCheck.ready
    ? [
        !consentCheck.checks?.formConsentPresent && 'opt-in checkbox/disclosure on the lead form',
        !consentCheck.checks?.noSharingClausePresent && 'no-third-party-sharing clause in the privacy policy',
      ].filter(Boolean)
    : [];

  // Open the review form pre-filled with the corrected values — does NOT submit.
  const openResubmitForm = async () => {
    setPreparing(true);
    setRepair(null);
    try {
      const res = await fetchWithCsrf(`/api/admin/a2p/resubmit/${tenant.tenant_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || data.error || `HTTP ${res.status}`);
      setForm({ useCase: data.useCase, ...data.fields });
    } catch (err) {
      setRepair({ mode: 'error', message: err.message });
    } finally {
      setPreparing(false);
    }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setSample = (i, v) => setForm(f => ({ ...f, sampleMessages: f.sampleMessages.map((s, idx) => (idx === i ? v : s)) }));
  const addSample = () => setForm(f => ({ ...f, sampleMessages: [...f.sampleMessages, ''] }));
  const removeSample = (i) => setForm(f => ({ ...f, sampleMessages: f.sampleMessages.filter((_, idx) => idx !== i) }));

  // Submit the (edited) form to carriers.
  const submitResubmit = async () => {
    setRepairing(true);
    setRepair(null);
    try {
      const overrides = {
        description: form.description,
        messageFlow: form.messageFlow,
        sampleMessages: (form.sampleMessages || []).filter(s => s && s.trim()),
        hasEmbeddedLinks: !!form.hasEmbeddedLinks,
        hasEmbeddedPhone: !!form.hasEmbeddedPhone,
        subscriberOptIn: !!form.subscriberOptIn,
        ageGated: !!form.ageGated,
        directLending: !!form.directLending,
      };
      const res = await fetchWithCsrf(`/api/admin/a2p/resubmit/${tenant.tenant_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || data.error || `HTTP ${res.status}`);
      setRepair(data);
      if (data.mode === 'api') { setForm(null); onResubmitted?.(); }
    } catch (err) {
      setRepair({ mode: 'error', message: err.message });
    } finally {
      setRepairing(false);
    }
  };

  return (
    <tr>
      <td colSpan={6} className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-3 gap-4">
          {/* Customer Profile */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer Profile</span>
            </div>
            {tenant.customer_profile ? (
              <div className="space-y-2">
                <StatusBadge status={tenant.customer_profile.status} />
                {tenant.customer_profile.twilio_sid && (
                  <p className="text-xs text-gray-400 font-mono truncate">{tenant.customer_profile.twilio_sid}</p>
                )}
                {tenant.customer_profile.updated_at && (
                  <p className="text-xs text-gray-400">Updated {formatDate(tenant.customer_profile.updated_at)}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Not submitted yet</p>
            )}
          </div>

          {/* Brand */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Brand</span>
            </div>
            {tenant.brand ? (
              <div className="space-y-2">
                <StatusBadge status={tenant.brand.status} />
                {tenant.brand.failure_reason && (
                  <p className="text-xs text-red-600 leading-snug">{tenant.brand.failure_reason}</p>
                )}
                {tenant.brand.twilio_sid && (
                  <p className="text-xs text-gray-400 font-mono truncate">{tenant.brand.twilio_sid}</p>
                )}
                {tenant.brand.updated_at && (
                  <p className="text-xs text-gray-400">Updated {formatDate(tenant.brand.updated_at)}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Not submitted yet</p>
            )}
          </div>

          {/* Campaign(s) */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Megaphone className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Campaign{tenant.campaigns.length !== 1 ? 's' : ''}
                {tenant.campaigns.length > 0 && (
                  <span className="ml-1 font-normal text-gray-400 normal-case">({tenant.campaigns.length})</span>
                )}
              </span>
            </div>
            {tenant.campaigns.length > 0 ? (
              <div className="space-y-3">
                {tenant.campaigns.map(c => (
                  <div key={c.id} className="space-y-1">
                    <StatusBadge status={c.status} />
                    {c.failure_reason && (
                      <p className="text-xs text-red-600 leading-snug">{c.failure_reason}</p>
                    )}
                    {c.twilio_sid && (
                      <p className="text-xs text-gray-400 font-mono truncate">{c.twilio_sid}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Not submitted yet</p>
            )}
          </div>
        </div>

        {/* Consent check — available for ANY tenant that's started A2P, not just rejected ones */}
        {tenant.customer_profile && (
          <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-gray-700">
                <Search className="inline w-4 h-4 text-gray-500 mr-1" />
                Check whether this customer’s website has the SMS consent language carriers require.
              </div>
              <button
                onClick={checkConsent}
                disabled={checking}
                className="px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {checking ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Check website
              </button>
            </div>

            {consentCheck && (
              <div className={`mt-3 text-sm px-3 py-2 rounded-lg border ${
                consentCheck.error ? 'bg-red-50 border-red-200 text-red-800'
                : consentCheck.ready ? 'bg-green-50 border-green-200 text-green-800'
                : consentCheck.scanned ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}>
                {consentCheck.error ? (
                  <span>Couldn’t check the site: {consentCheck.error}</span>
                ) : consentCheck.ready ? (
                  <span><CheckCircle className="inline w-4 h-4 mr-1" />Consent is live on the website — safe to resubmit.</span>
                ) : consentCheck.scanned ? (
                  <span>
                    <XCircle className="inline w-4 h-4 mr-1" />
                    Consent NOT found on the site yet{consentMissing.length ? `: still missing ${consentMissing.join(' and ')}` : ''}. The customer hasn’t added it — resubmitting now will likely be rejected again.
                  </span>
                ) : (
                  <span>Couldn’t reach or read the site ({consentCheck.reason || 'no result'}). Verify manually before resubmitting.</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Sender-pool attach — for approved campaigns whose number isn't pooled
            (legacy/edge tenants the cron auto-assign skips). Idempotent. */}
        {approvedCampaign && (
          <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-gray-700">
                <Phone className="inline w-4 h-4 text-gray-500 mr-1" />
                Attach this tenant’s number to its campaign’s sender pool so it can send (needed for legacy/edge tenants the auto-assign skips).
              </div>
              <button
                onClick={attachNumber}
                disabled={attaching}
                className="px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {attaching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                Attach number to pool
              </button>
            </div>
            {attachResult && (
              <div className={`mt-3 text-sm px-3 py-2 rounded-lg border ${attachResult.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                {attachResult.message}
              </div>
            )}
          </div>
        )}

        {/* Tier-3 rejection repair — recompute compliant copy + resubmit */}
        {failedCampaign && (
          <div className="mt-4 bg-white rounded-lg border border-red-200 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-gray-700">
                <Wrench className="inline w-4 h-4 text-red-600 mr-1" />
                Campaign rejected. Review the carrier-compliant copy, edit if needed, then resubmit (reuses the vetting fee, no recharge).
              </div>
              <button
                onClick={openResubmitForm}
                disabled={preparing || !!form}
                className="px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {preparing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
                Fix &amp; resubmit
              </button>
            </div>

            {/* Review-and-edit form — mirrors Twilio's campaign questions, pre-filled */}
            {form && (
              <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Review before resubmitting</div>

                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-1">Use case <span className="normal-case font-normal text-gray-400">(can’t change on an existing campaign)</span></label>
                  <input value={form.useCase || ''} readOnly className="w-full text-sm bg-gray-100 border border-gray-200 rounded px-2 py-1 text-gray-500" />
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-1">Campaign description</label>
                  <textarea value={form.description} onChange={e => setField('description', e.target.value)} rows={3} className="w-full text-sm border border-gray-300 rounded px-2 py-1" />
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-1">Opt-in / message flow</label>
                  <textarea value={form.messageFlow} onChange={e => setField('messageFlow', e.target.value)} rows={4} className="w-full text-sm border border-gray-300 rounded px-2 py-1" />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-medium text-gray-600">Sample messages</span>
                    <button onClick={addSample} className="text-[11px] text-indigo-600 hover:text-indigo-700">+ Add</button>
                  </div>
                  <div className="space-y-2">
                    {(form.sampleMessages || []).map((s, i) => (
                      <div key={i} className="flex gap-2">
                        <textarea value={s} onChange={e => setSample(i, e.target.value)} rows={2} className="flex-1 text-sm border border-gray-300 rounded px-2 py-1" />
                        <button onClick={() => removeSample(i)} title="Remove" className="text-gray-400 hover:text-red-600 text-sm px-1">✕</button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    ['hasEmbeddedLinks', 'Contains links'],
                    ['hasEmbeddedPhone', 'Contains phone'],
                    ['subscriberOptIn', 'Subscriber opt-in'],
                    ['ageGated', 'Age-gated'],
                    ['directLending', 'Direct lending'],
                  ].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1.5 text-xs text-gray-700">
                      <input type="checkbox" checked={!!form[k]} onChange={e => setField(k, e.target.checked)} />
                      {label}
                    </label>
                  ))}
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <button onClick={submitResubmit} disabled={repairing} className="px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-60 inline-flex items-center gap-2">
                    {repairing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
                    Submit to carriers
                  </button>
                  <button onClick={() => setForm(null)} disabled={repairing} className="px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-60">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {repair && (
              <div className="mt-3 space-y-2">
                <div className={`text-xs px-3 py-2 rounded-lg border ${
                  repair.mode === 'api' ? 'bg-green-50 border-green-200 text-green-800'
                  : repair.mode === 'error' ? 'bg-red-50 border-red-200 text-red-800'
                  : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}>
                  {repair.message}
                </div>
                {repair.mode === 'manual' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <CopyText label="Campaign description" value={repair.fields?.description} />
                    <CopyText label="Opt-in / message flow" value={repair.fields?.messageFlow} />
                    {Array.isArray(repair.fields?.sampleMessages) && (
                      <CopyText label="Sample messages" value={repair.fields.sampleMessages.join('\n\n')} />
                    )}
                    {repair.consoleUrl && (
                      <a href={repair.consoleUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 self-center">
                        <ExternalLink className="w-3 h-3" /> Open Twilio console to paste &amp; Update
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

const STAGE_FILTERS = [
  { value: 'all',              label: 'All Tenants'      },
  { value: 'not_started',      label: 'Not Started'      },
  { value: 'profile_pending',  label: 'Profile Pending'  },
  { value: 'profile_approved', label: 'Profile Approved' },
  { value: 'profile_failed',   label: 'Profile Failed'   },
  { value: 'brand_pending',    label: 'Brand Pending'    },
  { value: 'brand_approved',   label: 'Brand Approved'   },
  { value: 'brand_failed',     label: 'Brand Failed'     },
  { value: 'campaign_pending', label: 'Campaign Pending' },
  { value: 'campaign_failed',  label: 'Campaign Failed'  },
  { value: 'complete',         label: 'Complete'         },
];

// ─── Main page ────────────────────────────────────────────────────────────────

const A2PStatusDashboard = () => {
  const { user } = useAuth();
  const [tenants, setTenants]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [expanded, setExpanded]     = useState({});
  const [syncing, setSyncing]       = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (search) params.set('search', search);

      const res = await fetchWithCsrf(`/api/admin/a2p/tenants-status?${params}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setTenants(data.tenants || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const syncAndRefresh = async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await fetchWithCsrf('/api/admin/a2p/sync-all', { method: 'POST' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      await fetchData(true);
    } catch (err) {
      setError(`Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const filtered = stageFilter === 'all'
    ? tenants
    : tenants.filter(t => t.stage === stageFilter);

  const toggleExpand = (id) =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const counts = tenants.reduce((acc, t) => {
    acc[t.stage] = (acc[t.stage] || 0) + 1;
    return acc;
  }, {});
  const inProgress = tenants.length - (counts.complete || 0) - (counts.not_started || 0);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Shield className="w-8 h-8 text-indigo-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">A2P Registration Status</h1>
              <p className="text-sm text-gray-600 mt-1">Track every tenant's A2P compliance progress across all three steps</p>
            </div>
          </div>
          <button
            onClick={syncAndRefresh}
            disabled={syncing || refreshing}
            className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing || refreshing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing Twilio…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="p-6">

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Tenants</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{tenants.length}</p>
              </div>
              <Activity className="w-10 h-10 text-indigo-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Complete</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{counts.complete || 0}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{inProgress}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Not Started</p>
                <p className="text-2xl font-bold text-gray-500 mt-1">{counts.not_started || 0}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Table card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

          {/* Table toolbar */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Tenant A2P Status</h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchData()}
                  placeholder="Search tenant name…"
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <select
                value={stageFilter}
                onChange={e => setStageFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {STAGE_FILTERS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No tenants match your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer Profile</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map(tenant => (
                    <React.Fragment key={tenant.tenant_id}>
                      <tr
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => toggleExpand(tenant.tenant_id)}
                      >
                        {/* Tenant name */}
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">{tenant.tenant_name || tenant.tenant_id}</p>
                          {tenant.created_at && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {daysSince(tenant.created_at)}d old · joined {formatDate(tenant.created_at)}
                            </p>
                          )}
                        </td>

                        {/* Customer Profile */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={tenant.customer_profile?.status} />
                        </td>

                        {/* Brand */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={tenant.brand?.status} />
                        </td>

                        {/* Campaign */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <StatusBadge status={tenant.campaigns[0]?.status} />
                            {tenant.campaigns.length > 1 && (
                              <span className="text-xs text-gray-400">+{tenant.campaigns.length - 1}</span>
                            )}
                          </div>
                        </td>

                        {/* Progress dots */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StageProgress tenant={tenant} />
                        </td>

                        {/* Expand toggle */}
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {expanded[tenant.tenant_id]
                            ? <ChevronUp className="w-4 h-4 text-gray-400 inline" />
                            : <ChevronDown className="w-4 h-4 text-gray-400 inline" />
                          }
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {expanded[tenant.tenant_id] && <TenantDetail tenant={tenant} onResubmitted={() => fetchData(true)} />}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default A2PStatusDashboard;
